import React, { useState, useEffect } from 'react';
import validator from 'validator';

import Button from 'components/Button';
import SelectInput from 'components/SelectInput';

import { FormInput, FormContain, InputContain, FormCheckBox } from './Styled';

export const Fields = ({
  values,
  children,
  onFieldBlur,
  errors = [],
  renderSubmit
}) => {
  const [state, setState] = useState(values || INITIAL_STATE);

  useEffect(() => {
    setState((state) => ({ ...state, ...(values || {}) }));
  }, [values]);

  const setFieldValue = (name, value) =>
    setState((state) => ({ ...state, [name]: value }));

  const validateFields = () => {
    var errors = React.Children.map(children, (child) =>
      validateField(child.props)
    ).filter((error) => !!error);

    return (
      errors.length &&
      errors.reduce((errors, error) => ({ ...errors, ...error }), {})
    );
  };

  const submitForm = () => {
    const validationErrors = validateFields();

    return {
      values: state,
      errors: validationErrors,
      isValid: !validationErrors
    };
  };

  const mapPropsChildren = () => {
    const onInput = (name, value) => {
      setFieldValue(name, value, setState);
    };

    return React.Children.map(children, (child) =>
      React.cloneElement(child, {
        values: state,
        setValue: onInput,
        onBlur: onFieldBlur,
        errorText: errors && errors[child.props.name]
      })
    );
  };

  const validateField = ({ validate, name, label, type, displayName, required }) => {
    switch (false) {
      case validateRequired(state[name], required):
        return mapRequiredError(name, label, displayName);
      case validateType(state[name], type):
        return mapTypeError(name, label, type);
      case !(validate && isFunction(validate)):
        return validate(state);
      default:
        return false;
    }
  };

  return (
    <FormContain>
      <InputContain>{mapPropsChildren()}</InputContain>
      {renderSubmit && renderSubmit(submitForm)}
    </FormContain>
  );
};

export const Submit = ({ values, color, icon, label, onSubmit }) => {
  return (
    <Button.Primary
      icon={icon}
      label={label}
      onClick={() => onSubmit && onSubmit(values)}
    />
  );
};

export const TextInput = ({ name, values, onBlur, setValue, ...props }) => {
  return (
    <FormInput
      {...props}
      name={name}
      onBlur={onBlur}
      onChange={setValue}
      value={values[name]}
    />
  );
};

export const CheckBox = ({ name, values, setValue, ...props }) => {
  return (
    <FormCheckBox {...props} name={name} onClick={setValue} value={values[name]} />
  );
};

export const Select = ({ name, values, onBlur, setValue, ...props }) => {
  return (
    <SelectInput
      {...props}
      name={name}
      value={values[name] || ''}
      onBlur={(e) => onBlur(name, e.target.value)}
      onChange={(e) => setValue(name, e.target.value)}
    >
      {!values[name] && <option value="" disabled />}
      {props.children}
    </SelectInput>
  );
};

export const Option = ({ placeholder, value, children }) => {
  return <option>{children}</option>;
};

const INITIAL_STATE = {};

const FRIENDLY_TYPES = {
  email: 'email address',
  tel: 'telephone number'
};

const validateType = (value = '', type) => {
  switch (type) {
    case 'email':
      return validator.isEmail(value);
    case 'tel':
      return validator.isInt(value);
    default:
      return true;
  }
};

const isFunction = (func) => typeof func === 'function';

const getFriendlyType = (type) => FRIENDLY_TYPES[type];

const validateRequired = (value = '', required) => !required || !!value;

const mapRequiredError = (name, label, displayName) => ({
  [name]: `${displayName || label} is required.`
});

const mapTypeError = (name, label, type) => ({
  [name]: `${label} must be a valid ${getFriendlyType(type)}.`
});
